import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSalaryHistory, useNegotiationHistory } from "@/hooks/useSalaryHistory";
import { formatDistanceToNow } from "date-fns";
import { Clock, MapPin, Briefcase, DollarSign, TrendingUp, MessageSquare, ChevronRight, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function HistoryPanel() {
  const { data: analyses, isLoading: analysesLoading } = useSalaryHistory();
  const { data: scenarios, isLoading: scenariosLoading } = useNegotiationHistory();
  const [selectedAnalysis, setSelectedAnalysis] = useState<any>(null);
  const [selectedScenario, setSelectedScenario] = useState<any>(null);

  if (analysesLoading || scenariosLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Alert>
        <Clock className="h-4 w-4" />
        <AlertDescription>
          View and reuse your past salary analyses and negotiation scenarios.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="analyses" className="space-y-4">
        <TabsList>
          <TabsTrigger value="analyses">
            Market Analyses ({analyses?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="scenarios">
            Negotiation Scenarios ({scenarios?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analyses" className="space-y-4">
          {!analyses || analyses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Market Analyses Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Your salary market analyses will appear here once you create them.
                </p>
                <Button>Create Your First Analysis</Button>
              </CardContent>
            </Card>
          ) : (
            analyses.map((analysis) => {
              const marketData = analysis.market_data as any;
              const avgSalary = marketData?.average || marketData?.percentiles?.p50;
              
              return (
                <Card key={analysis.id} className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-xl flex items-center gap-2">
                          <Briefcase className="h-5 w-5 flex-shrink-0" />
                          <span className="truncate">{analysis.job_title}</span>
                        </CardTitle>
                        <CardDescription className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2">
                          <span className="flex items-center gap-1 truncate">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{analysis.location}</span>
                          </span>
                          <span className="whitespace-nowrap">{analysis.years_experience} years exp</span>
                          {analysis.industry && (
                            <Badge variant="secondary" className="truncate max-w-[150px]">{analysis.industry}</Badge>
                          )}
                        </CardDescription>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedAnalysis(analysis)}
                      >
                        View Details
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm text-muted-foreground">Market Average</div>
                        <div className="text-xl sm:text-2xl font-bold text-primary truncate">
                          ${avgSalary?.toLocaleString()}
                        </div>
                      </div>
                      {analysis.current_salary && (
                        <div>
                          <div className="text-sm text-muted-foreground">Your Salary</div>
                          <div className="text-xl sm:text-2xl font-bold truncate">
                            ${analysis.current_salary.toLocaleString()}
                          </div>
                        </div>
                      )}
                      <div className="col-span-1 sm:col-span-2 lg:col-span-1">
                        <div className="text-sm text-muted-foreground">Created</div>
                        <div className="text-sm truncate">
                          {formatDistanceToNow(new Date(analysis.created_at), { addSuffix: true })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        <TabsContent value="scenarios" className="space-y-4">
          {!scenarios || scenarios.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Negotiation Scenarios Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Your negotiation strategies will appear here once you generate them.
                </p>
                <Button>Generate Your First Strategy</Button>
              </CardContent>
            </Card>
          ) : (
            scenarios.map((scenario) => (
              <Card key={scenario.id} className="hover:shadow-md transition-shadow cursor-pointer">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-xl flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 flex-shrink-0" />
                        <span className="truncate">Negotiation Strategy</span>
                      </CardTitle>
                      <CardDescription className="mt-2 truncate">
                        Created {formatDistanceToNow(new Date(scenario.created_at), { addSuffix: true })}
                      </CardDescription>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedScenario(scenario)}
                    >
                      View Strategy
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Offer Amount</div>
                      <div className="text-xl sm:text-2xl font-bold truncate">
                        ${scenario.offer_amount.toLocaleString()}
                      </div>
                    </div>
                    {scenario.counter_offer && (
                      <div>
                        <div className="text-sm text-muted-foreground">Counter Offer</div>
                        <div className="text-xl sm:text-2xl font-bold text-primary truncate">
                          ${scenario.counter_offer.toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                  {scenario.talking_points && Array.isArray(scenario.talking_points) && scenario.talking_points.length > 0 && (
                    <div className="mt-4">
                      <div className="text-sm text-muted-foreground mb-2">Talking Points</div>
                      <div className="flex gap-2 flex-wrap">
                        {scenario.talking_points.slice(0, 3).map((point: string, i: number) => (
                          <Badge key={i} variant="outline" className="truncate max-w-[200px]">{point}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Analysis Detail Dialog */}
      <Dialog open={!!selectedAnalysis} onOpenChange={() => setSelectedAnalysis(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              {selectedAnalysis?.job_title}
            </DialogTitle>
            <DialogDescription>
              {selectedAnalysis?.location} • {selectedAnalysis?.years_experience} years experience
            </DialogDescription>
          </DialogHeader>
          {selectedAnalysis && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-muted-foreground">Market Average</div>
                    <div className="text-3xl font-bold text-primary">
                      ${((selectedAnalysis.market_data as any)?.average || (selectedAnalysis.market_data as any)?.percentiles?.p50)?.toLocaleString()}
                    </div>
                  </CardContent>
                </Card>
                {selectedAnalysis.current_salary && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Your Salary</div>
                      <div className="text-3xl font-bold">
                        ${selectedAnalysis.current_salary.toLocaleString()}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {(selectedAnalysis.market_data as any)?.percentiles && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Salary Percentiles</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 gap-2 text-center">
                      {Object.entries((selectedAnalysis.market_data as any).percentiles).map(([key, value]) => (
                        <div key={key}>
                          <div className="text-xs text-muted-foreground">{key.toUpperCase()}</div>
                          <div className="font-semibold">${(value as number / 1000).toFixed(0)}k</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {selectedAnalysis.skills && Array.isArray(selectedAnalysis.skills) && selectedAnalysis.skills.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Skills Included</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {selectedAnalysis.skills.map((skill: string, i: number) => (
                        <Badge key={i} variant="secondary">{skill}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Scenario Detail Dialog */}
      <Dialog open={!!selectedScenario} onOpenChange={() => setSelectedScenario(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Negotiation Strategy
            </DialogTitle>
            <DialogDescription>
              Created {selectedScenario && formatDistanceToNow(new Date(selectedScenario.created_at), { addSuffix: true })}
            </DialogDescription>
          </DialogHeader>
          {selectedScenario && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-muted-foreground">Offer Amount</div>
                    <div className="text-3xl font-bold">
                      ${selectedScenario.offer_amount.toLocaleString()}
                    </div>
                  </CardContent>
                </Card>
                {selectedScenario.counter_offer && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Counter Offer</div>
                      <div className="text-3xl font-bold text-primary">
                        ${selectedScenario.counter_offer.toLocaleString()}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {selectedScenario.negotiation_strategy && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Strategy</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="whitespace-pre-wrap text-sm">{selectedScenario.negotiation_strategy}</pre>
                  </CardContent>
                </Card>
              )}

              {selectedScenario.talking_points && Array.isArray(selectedScenario.talking_points) && selectedScenario.talking_points.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Talking Points</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside space-y-2">
                      {selectedScenario.talking_points.map((point: string, i: number) => (
                        <li key={i}>{point}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {selectedScenario.email_templates && Array.isArray(selectedScenario.email_templates) && selectedScenario.email_templates.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Email Templates</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedScenario.email_templates.map((template: string, i: number) => (
                      <div key={i} className="p-4 bg-muted rounded-lg">
                        <pre className="whitespace-pre-wrap text-sm">{template}</pre>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
